import { Category, CategoryGroup, Subcategory } from '../types/api';

/**
 * Работа с трёхуровневой таксономией: супер-категория → категория → подкатегория.
 *
 * Сервер принимает только те подкатегории, которые лежат в супер-категориях
 * выбранных категорий: барбершопу нельзя проставить «Итальянскую кухню».
 * Нерелевантные id он отбрасывает молча, поэтому те же правила продублированы
 * здесь — иначе партнёр сохранит выбор и не поймёт, почему он пропал.
 */

/** Все категории дерева одним плоским списком, без дублей и в порядке position. */
export function flattenCategories(groups: CategoryGroup[]): Category[] {
  const byId = new Map<number, Category>();

  groups.forEach((group) => {
    group.categories.forEach((category) => {
      if (!byId.has(category.id)) byId.set(category.id, category);
    });
  });

  return Array.from(byId.values()).sort((a, b) => a.position - b.position);
}

/** Раздел в выборе категории заведения: заголовок и категории под ним. */
export interface CategorySection {
  /** id супер-категории; `other` — категории вне разделов, `flat` — список без разделов. */
  key: number | 'other' | 'flat';
  /** Заголовок раздела. `null` — плоский список: разделов нет, заголовка тоже. */
  title: string | null;
  categories: Category[];
}

const byPosition = (a: { position: number }, b: { position: number }) => a.position - b.position;

/**
 * Категории по разделам — из них партнёр выбирает категорию заведения.
 *
 * Сплошной список в полсотни пунктов не читается: на проде аптека выбрала
 * «Прочее», не найдя «Аптеки». Поэтому:
 * - разделы и категории внутри идут по `position` — порядок ответа не важен;
 * - категория из нескольких разделов («Бары и пабы» — и в «Поесть», и в
 *   «Развлечениях») стоит один раз, в первом по `position` разделе: дубль в
 *   `<select>` — два пункта с одним значением;
 * - категории плоского справочника, не вошедшие ни в один раздел, — в конце
 *   разделом `otherTitle`;
 * - разделов нет (дерево не загрузилось или пустое) — плоский список без
 *   заголовка, как до разделов: выбрать категорию можно всегда.
 *
 * Исходные массивы не трогает.
 */
export function categorySections(
  groups: CategoryGroup[],
  categories: Category[],
  otherTitle: string,
): CategorySection[] {
  const seen = new Set<number>();

  const sections: CategorySection[] = [...groups]
    .sort(byPosition)
    .map((group) => ({
      key: group.id,
      title: group.title,
      categories: [...group.categories].sort(byPosition).filter((category) => {
        if (seen.has(category.id)) return false;
        seen.add(category.id);
        return true;
      }),
    }))
    .filter((section) => section.categories.length > 0);

  if (sections.length === 0) {
    const flat = [...categories].sort(byPosition);

    return flat.length > 0 ? [{ key: 'flat', title: null, categories: flat }] : [];
  }

  const rest = categories.filter((category) => !seen.has(category.id)).sort(byPosition);

  return rest.length > 0 ? [...sections, { key: 'other', title: otherTitle, categories: rest }] : sections;
}

/** Супер-категории, в которые входит хотя бы одна из выбранных категорий. */
export function relevantGroups(groups: CategoryGroup[], categoryIds: number[]): CategoryGroup[] {
  if (categoryIds.length === 0) return [];

  const selected = new Set(categoryIds);

  return groups.filter((group) => group.categories.some((category) => selected.has(category.id)));
}

/**
 * Подкатегории, доступные при текущем наборе категорий — ровно то, что примет
 * сервер. Без дублей: одна категория может входить в несколько групп.
 */
export function allowedSubcategories(groups: CategoryGroup[], categoryIds: number[]): Subcategory[] {
  const byId = new Map<number, Subcategory>();

  relevantGroups(groups, categoryIds).forEach((group) => {
    group.subcategories.forEach((subcategory) => {
      if (!byId.has(subcategory.id)) byId.set(subcategory.id, subcategory);
    });
  });

  return Array.from(byId.values()).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/**
 * Убирает подкатегории, ставшие нерелевантными после смены категорий.
 * Возвращает новый массив; исходный не трогает.
 */
export function pruneSubcategories(
  groups: CategoryGroup[],
  categoryIds: number[],
  subcategoryIds: number[],
): number[] {
  const allowed = new Set(allowedSubcategories(groups, categoryIds).map((s) => s.id));

  return subcategoryIds.filter((id) => allowed.has(id));
}

/**
 * Полный набор категорий заведения: основная плюс дополнительные.
 * Основная всегда первая и не дублируется.
 */
export function allCategoryIds(primaryId: number | null, categoryIds: number[]): number[] {
  const additional = categoryIds.filter((id) => id !== primaryId);

  return primaryId === null ? additional : [primaryId, ...additional];
}
